import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listGoodsReceipts, listPurchaseOrders } from '../../api/purchasing';
import { listStockBalances, listStockLedger } from '../../api/inventory';
import { listExpiryLots, listStockCounts, listTransfers, listWasteEvents } from '../../api/inventoryOps';
import { listItems } from '../../api/masterData';
import { usePermissions } from '../../context/PermissionContext';
import { useBranch } from '../../context/BranchContext';
import { PoDetailDrawer } from '../../components/PoDetailDrawer';
import { Button, ClickableRow, DashboardSkeleton, EmptyState, ErrorNote, KPICard, SectionCard, StatusBadge, tableStyle, tdStyle, thStyle } from '../../components/common';
import { IconAlertTriangle, IconArchive, IconClipboardCheck, IconRepeat, IconTrash, IconTruck } from '../../components/icons';
import type { ExpiryLot, GoodsReceipt, Item, PurchaseOrder, StockBalance, StockLedgerEntry } from '../../types/domain';

/**
 * Warehouse & Receiving workspace. Everything on this page is REAL data
 * (same api/* adapters the vertical-slice pages use). Stock Opname,
 * Transfer, and Waste create/post screens (migration 0033) are now live —
 * their KPI cards link straight to those pages.
 */
export default function WarehouseDashboard() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { activeBranchId, activeBranchWarehouseIds } = useBranch();
  const [grs, setGrs] = useState<GoodsReceipt[] | null>(null);
  const [pos, setPos] = useState<PurchaseOrder[] | null>(null);
  const [balances, setBalances] = useState<StockBalance[] | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [recentMovements, setRecentMovements] = useState<StockLedgerEntry[] | null>(null);
  const [countsPending, setCountsPending] = useState<number | null>(null);
  const [transfersPending, setTransfersPending] = useState<number | null>(null);
  const [wastePending, setWastePending] = useState<number | null>(null);
  const [expiryLots, setExpiryLots] = useState<ExpiryLot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPo, setSelectedPo] = useState<PurchaseOrder | null>(null);

  const canReadGr = hasPermission('goods_receipt', 'read');
  const canReadBalance = hasPermission('stock_balance', 'read');
  const canReadLedger = hasPermission('stock_ledger', 'read');
  const canReadExpiry = hasPermission('expiry_lot', 'read');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      canReadGr ? listGoodsReceipts(activeBranchId ?? undefined) : Promise.resolve(null),
      canReadGr ? listPurchaseOrders(activeBranchId ?? undefined) : Promise.resolve(null),
      canReadBalance ? listStockBalances(undefined, activeBranchId ? activeBranchWarehouseIds : undefined) : Promise.resolve(null),
      canReadBalance ? listItems() : Promise.resolve([]),
      canReadLedger ? listStockLedger(undefined, activeBranchId ?? undefined) : Promise.resolve(null),
      listStockCounts(activeBranchId ?? undefined).catch(() => []),
      listTransfers(activeBranchId ? activeBranchWarehouseIds : undefined).catch(() => []),
      listWasteEvents(activeBranchId ?? undefined).catch(() => []),
      canReadExpiry ? listExpiryLots(activeBranchId ?? undefined).catch(() => []) : Promise.resolve([]),
    ])
      .then(([grRows, poRows, balanceRows, itemRows, ledgerRows, counts, transfers, waste, lots]) => {
        if (cancelled) return;
        setGrs(grRows);
        setPos(poRows);
        setBalances(balanceRows);
        setItems(itemRows ?? []);
        setRecentMovements(ledgerRows ? ledgerRows.slice(0, 8) : null);
        setCountsPending(counts.filter((c) => c.status === 'DRAFT').length);
        setTransfersPending(transfers.filter((t) => t.status === 'DRAFT').length);
        setWastePending(waste.filter((w) => w.status === 'DRAFT').length);
        setExpiryLots(lots);
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canReadGr, canReadBalance, canReadLedger, canReadExpiry, activeBranchId, activeBranchWarehouseIds.join(',')]);

  if (loading) return <DashboardSkeleton />;
  if (error) return <ErrorNote text={error} />;

  const itemsById = Object.fromEntries(items.map((it) => [it.item_id, it]));
  const receivablePos = pos ? pos.filter((po) => po.status === 'ISSUED' || po.status === 'PARTIALLY_RECEIVED') : [];
  const draftGrs = grs ? grs.filter((gr) => gr.status === 'DRAFT') : [];
  const lowStock = balances ? balances.filter((b) => b.qty_on_hand <= 5) : [];
  const upcomingExpiry = [...expiryLots].filter((l) => l.qty_remaining > 0).sort((a, b) => a.expiry_date.localeCompare(b.expiry_date));

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--alex-card-gap)', marginBottom: 'var(--alex-card-gap)' }}>
        {canReadGr && <KPICard label="PO siap diterima" value={receivablePos.length} hint="ISSUED / PARTIALLY_RECEIVED" icon={<IconTruck size={16} />} tone="primary" linkTo="/app/goods-receipts" />}
        {canReadGr && <KPICard label="GR draft" value={draftGrs.length} hint="belum posted" icon={<IconClipboardCheck size={16} />} tone="warning" linkTo="/app/goods-receipts" />}
        <KPICard label="Stock Opname draft" value={countsPending} icon={<IconArchive size={16} />} tone="info" linkTo="/app/stock-opname" />
        <KPICard label="Transfer draft" value={transfersPending} icon={<IconRepeat size={16} />} tone="info" linkTo="/app/transfers" />
        <KPICard label="Waste draft" value={wastePending} icon={<IconTrash size={16} />} tone="danger" linkTo="/app/waste" />
        {canReadBalance && <KPICard label="Item stok rendah" value={lowStock.length} hint="qty ≤ 5" icon={<IconAlertTriangle size={16} />} tone="danger" linkTo="/app/stock-balance" />}
      </div>

      <div className="alex-grid-8-4">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--alex-card-gap)' }}>
          {canReadGr && (
            <SectionCard title="Goods Receipt Queue" action="/app/goods-receipts">
              {receivablePos.length === 0 ? (
                <EmptyState text="Tidak ada PO menunggu penerimaan." variant="compact" />
              ) : (
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>No. PO</th>
                      <th style={thStyle}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {receivablePos.slice(0, 6).map((po) => (
                      <ClickableRow key={po.po_id} onClick={() => setSelectedPo(po)}>
                        <td style={tdStyle}>{po.po_number}</td>
                        <td style={tdStyle}>
                          <StatusBadge status={po.status} />
                        </td>
                      </ClickableRow>
                    ))}
                  </tbody>
                </table>
              )}
            </SectionCard>
          )}

          {canReadLedger && recentMovements && recentMovements.length > 0 && (
            <SectionCard title="Stock Ledger Terbaru" action="/app/stock-ledger">
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Waktu</th>
                    <th style={thStyle}>Tipe</th>
                    <th style={thStyle}>Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {recentMovements.map((m) => (
                    <ClickableRow key={m.ledger_id} onClick={() => navigate(`/app/stock-ledger?item=${m.item_id}`)}>
                      <td style={tdStyle}>{new Date(m.occurred_at).toLocaleString('id-ID')}</td>
                      <td style={tdStyle}>{m.movement_type}</td>
                      <td style={{ ...tdStyle, color: m.qty_delta >= 0 ? 'var(--alex-success)' : 'var(--alex-danger)', fontWeight: 600 }}>
                        {m.qty_delta >= 0 ? '+' : ''}
                        {m.qty_delta}
                      </td>
                    </ClickableRow>
                  ))}
                </tbody>
              </table>
            </SectionCard>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--alex-card-gap)' }}>
          {canReadBalance && (
            <SectionCard title="Item Stok Rendah" action="/app/stock-balance">
              {lowStock.length === 0 ? (
                <EmptyState text="Tidak ada item stok rendah." variant="compact" />
              ) : (
                <table style={tableStyle}>
                  <tbody>
                    {lowStock.slice(0, 5).map((b) => (
                      <ClickableRow key={`${b.warehouse_id}-${b.item_id}`} onClick={() => navigate(`/app/stock-ledger?item=${b.item_id}`)}>
                        <td style={tdStyle}>{itemsById[b.item_id]?.item_code ?? b.item_id.slice(0, 8)}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--alex-danger)', fontWeight: 700 }}>{b.qty_on_hand}</td>
                      </ClickableRow>
                    ))}
                  </tbody>
                </table>
              )}
            </SectionCard>
          )}

          <SectionCard title="Aksi Cepat">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              <Button variant="secondary" style={{ justifyContent: 'flex-start', width: '100%' }} onClick={() => navigate('/app/goods-receipts')}>
                Terima Barang (GR)
              </Button>
              <Button variant="secondary" style={{ justifyContent: 'flex-start', width: '100%' }} onClick={() => navigate('/app/stock-opname')}>
                Mulai Stock Opname
              </Button>
              <Button variant="secondary" style={{ justifyContent: 'flex-start', width: '100%' }} onClick={() => navigate('/app/transfers')}>
                Buat Transfer
              </Button>
              <Button variant="secondary" style={{ justifyContent: 'flex-start', width: '100%' }} onClick={() => navigate('/app/waste')}>
                Laporkan Waste
              </Button>
            </div>
          </SectionCard>

          {canReadExpiry && (
            <SectionCard title="Lot & Expiry Terdekat" action="/app/expiry">
              {upcomingExpiry.length === 0 ? (
                <EmptyState text="Tidak ada lot dengan sisa stok." variant="compact" />
              ) : (
                <table style={tableStyle}>
                  <tbody>
                    {upcomingExpiry.slice(0, 5).map((l) => (
                      <tr key={l.lot_id}>
                        <td style={tdStyle}>{l.lot_no}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{l.expiry_date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </SectionCard>
          )}
        </div>
      </div>

      <PoDetailDrawer po={selectedPo} open={!!selectedPo} onClose={() => setSelectedPo(null)} />
    </div>
  );
}
